import {
  TextInput,
  ArrayInput,
  SimpleFormIterator,
  RadioButtonGroupInput,
  ArrayField,
  BooleanInput,
  useStore,
} from "react-admin";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import { useWatch } from "react-hook-form";
import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import NiyaazHofLookup from "./NiyaazHofLookup";
import { NiyaazVenueFields } from "./NiyaazVenueFields";
import { NiyaazDataGrid } from "./NiyaazDataGrid";
import { NiyaazMemberRowFields } from "./NiyaazMemberRowFields";
import { NiyaazFormSection } from "./NiyaazFormSection";
import { isAsharaEvent } from "@/utils";
import { formatINR } from "@/utils";
import type { CurrentEvent } from "@/containers/events/types";

const readOnlyInputSx = {
  opacity: 0.4,
  pointerEvents: "none",
  cursor: "default",
  "&:hover": { opacity: 0.4 },
} as const;

type NiyaazHofSectionProps = {
  isAshara: boolean;
  showLookup?: boolean;
};

export const NiyaazHofSection = ({ isAshara, showLookup = false }: NiyaazHofSectionProps) => (
  <NiyaazFormSection title="HOF Details" action={showLookup ? <NiyaazHofLookup /> : undefined}>
    <Grid container spacing={2}>
      <NiyaazVenueFields isAshara={isAshara} />
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextInput source="HOFId" label="HOF ITS" fullWidth isRequired />
      </Grid>
      <Grid size={12}>
        <TextInput source="HOFName" label="Full Name" fullWidth isRequired />
      </Grid>
      <Grid size={12}>
        <TextInput source="HOFAddress" label="Address" fullWidth isRequired />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextInput source="HOFPhone" label="Phone" fullWidth isRequired />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <BooleanInput
          source="sendReminders"
          label="Send reminders"
          fullWidth
          defaultValue={false}
          helperText="Send WhatsApp contribution reminder messages for outstanding balance"
        />
      </Grid>
    </Grid>
  </NiyaazFormSection>
);

type NiyaazTakhmeenSectionProps = {
  isAshara: boolean;
  pirsaDefaultValue?: number;
};

export const NiyaazTakhmeenSection = ({
  isAshara,
  pirsaDefaultValue = 0,
}: NiyaazTakhmeenSectionProps) => {
  const [currentEvent] = useStore<CurrentEvent | null>("currentEvent", null);
  const chairs = useWatch({ name: "chairs" });
  const zabihat = useWatch({ name: "zabihat" });
  const mehmaanCount = useWatch({ name: "mehmaanCount" });
  const relayRequired = useWatch({ name: "relayRequired" });
  const pirsa = useWatch({ name: "pirsa" });
  const pirsaRequired = useWatch({ name: "pirsaRequired" });

  const ez = currentEvent?.zabihatAmount ?? 0;
  const ec = currentEvent?.chairAmount ?? 0;
  const em = currentEvent?.mehmaanAmount ?? 0;
  const er = currentEvent?.relayAmount ?? 0;
  const ep = currentEvent?.pirsaAmount ?? 0;

  return (
    <NiyaazFormSection title="Takhmeen Details">
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <NoArrowKeyNumberInput
            source="takhmeenAmount"
            label={isAshara ? "Niyaaz amount" : "Takhmeen amount"}
            fullWidth
            defaultValue={0}
          />
        </Grid>
        {!isAshara && (
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <NoArrowKeyNumberInput source="iftaari" fullWidth defaultValue={0} />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <NoArrowKeyNumberInput
            source="zabihat"
            fullWidth
            defaultValue={0}
            helperText={`${zabihat} X ₹${ez} = ₹${Number(zabihat) * ez}`}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <NoArrowKeyNumberInput
            source="chairs"
            fullWidth
            defaultValue={0}
            helperText={`Toggle chair per family member or mehmaan. ${chairs} X ₹${ec} = ₹${Number(chairs) * ec}`}
            slotProps={{ input: { readOnly: true, sx: readOnlyInputSx } }}
            disabled
          />
        </Grid>
        {isAshara && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NoArrowKeyNumberInput
                source="mehmaanCount"
                label="Mehmaans"
                fullWidth
                defaultValue={0}
                helperText={`${mehmaanCount ?? 0} X ₹${em} = ₹${Number(mehmaanCount ?? 0) * em}`}
                disabled
              />
            </Grid>
            <BooleanInput source="relayRequired" sx={{ display: "none" }} defaultValue={false} />
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                Relay:{" "}
                <Typography component="span" variant="body2" color="text.primary" fontWeight={600}>
                  {relayRequired ? (er > 0 ? `1 X ₹${er} = ₹${er}` : "Yes") : "No"}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Toggle relay per family member or mehmaan. Max 1 unit per registration.
              </Typography>
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <BooleanInput
            source="pirsaRequired"
            label="Pirsa required"
            fullWidth
            defaultValue={false}
            helperText="Include pirsa contribution for this registration"
          />
        </Grid>
        {pirsaRequired && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <NoArrowKeyNumberInput
                source="pirsa"
                label="Pirsa"
                fullWidth
                defaultValue={pirsaDefaultValue}
                helperText={`${pirsa ?? 0} X ₹${ep} = ₹${Number(pirsa) * ep}`}
                validate={[
                  (value: unknown) => (Number(value) > 0 ? undefined : "Pirsa count is required"),
                ]}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <RadioButtonGroupInput
                source="pirsaSelfPickup"
                label="Pirsa fulfillment"
                fullWidth
                row
                choices={[
                  { id: "false", name: "Delivery needed" },
                  { id: "true", name: "Self pickup" },
                ]}
                defaultValue="false"
                format={(value: boolean | undefined) => (value ? "true" : "false")}
                parse={(value: string) => value === "true"}
                helperText="Delivery to the address above unless self pickup is selected"
              />
            </Grid>
          </>
        )}
      </Grid>
    </NiyaazFormSection>
  );
};

type NiyaazPayableSummaryProps = {
  payable: number;
  paidAmount?: number;
};

export const NiyaazPayableSummary = ({
  payable,
  paidAmount: paidAmountProp,
}: NiyaazPayableSummaryProps) => {
  const watchedPaid = Number(useWatch({ name: "paidAmount" })) || 0;
  const paid = paidAmountProp ?? watchedPaid;
  const balance = payable - paid;

  return (
    <NiyaazFormSection title="Summary">
      <Card variant="outlined" sx={{ bgcolor: "action.hover", width: "100%", boxShadow: "none" }}>
        <CardContent sx={{ py: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Payable
              </Typography>
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {formatINR(payable)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Paid
              </Typography>
              <Typography variant="h6" fontWeight={600}>
                {formatINR(paid)}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 4 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Balance
              </Typography>
              <Typography
                variant="h6"
                fontWeight={600}
                color={balance > 0 ? "warning.main" : "success.main"}
              >
                {formatINR(balance)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </NiyaazFormSection>
  );
};

export const NiyaazCreatePaymentSection = () => (
  <NiyaazFormSection title="Payment">
    <Stack spacing={2}>
      <NoArrowKeyNumberInput source="total" sx={{ display: "none" }} defaultValue={0} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <NoArrowKeyNumberInput
            label="Paid Amount"
            source="paidAmount"
            fullWidth
            defaultValue={0}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <RadioButtonGroupInput
            source="mode"
            choices={[
              { id: "CASH", name: "CASH" },
              { id: "ONLINE", name: "ONLINE" },
              { id: "CHEQUE", name: "CHEQUE" },
            ]}
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput source="details" label="Payment Details" fullWidth />
        </Grid>
      </Grid>
    </Stack>
  </NiyaazFormSection>
);

export const NiyaazCommentsSection = () => (
  <NiyaazFormSection title="Comments">
    <TextInput source="comments" fullWidth label="Comments" multiline minRows={3} />
  </NiyaazFormSection>
);

const memberIteratorSx = {
  width: "100%",
  "& .RaSimpleFormIterator-form": { width: "100%", flex: 1, minWidth: 0 },
  "& .RaSimpleFormIterator-line": { alignItems: "flex-start", minWidth: 0 },
} as const;

type NiyaazPeopleSectionProps = {
  isAshara: boolean;
};

export const NiyaazPeopleSection = ({ isAshara }: NiyaazPeopleSectionProps) => (
  <Stack spacing={2}>
    <NiyaazFormSection title="Family Members">
      <ArrayInput source="familyMembers" fullWidth label="">
        <SimpleFormIterator fullWidth sx={memberIteratorSx}>
          <NiyaazMemberRowFields showRelay={isAshara} />
        </SimpleFormIterator>
      </ArrayInput>
    </NiyaazFormSection>
    {isAshara && (
      <NiyaazFormSection title="Mehmaans">
        <ArrayInput source="mehmaans" fullWidth label="">
          <SimpleFormIterator fullWidth sx={memberIteratorSx}>
            <NiyaazMemberRowFields showJamaat showRelay={isAshara} showWhatsApp />
          </SimpleFormIterator>
        </ArrayInput>
      </NiyaazFormSection>
    )}
  </Stack>
);

export const NiyaazPreviousHistorySection = () => {
  const previousHistory = useWatch({ name: "previousHistory" });
  const previousHistoryRows = (previousHistory as { rows?: unknown[] } | null | undefined)?.rows;
  const hasPreviousHistoryRows =
    Array.isArray(previousHistoryRows) && previousHistoryRows.length > 0;

  if (!previousHistory) {
    return null;
  }

  return (
    <NiyaazFormSection title="Previous Takhmeen History">
      {hasPreviousHistoryRows ? (
        <ArrayField record={previousHistory as Record<string, unknown>} source="rows">
          <NiyaazDataGrid />
        </ArrayField>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No Previous Records Found
        </Typography>
      )}
    </NiyaazFormSection>
  );
};

export const useIsAsharaNiyaazForm = (currentEvent: CurrentEvent | null | undefined) =>
  isAsharaEvent(currentEvent ?? undefined);
